import * as THREE from 'three';

/**
 * 紧凑特征面几何构建输入。
 */
export interface CreateCompactIndexedFaceGeometryInput {
  /** 提供三角形数据的源几何。 */
  sourceGeometry: THREE.BufferGeometry;
  /** 需要提取的三角形半开区间。 */
  triangleRange: [number, number];
  /** 是否复制法线属性；默认复制，缺失时自动跳过。 */
  includeNormal?: boolean;
}

/**
 * 判断属性是否可按三维向量读取。
 *
 * @param value 待判断属性。
 * @returns 是否为可读取三维向量属性。
 */
function isReadableVectorAttribute(
  value: unknown,
): value is THREE.BufferAttribute | THREE.InterleavedBufferAttribute {
  return (value instanceof THREE.BufferAttribute || value instanceof THREE.InterleavedBufferAttribute)
    && value.itemSize >= 3
    && value.count > 0;
}

/**
 * 读取源三角形中的顶点索引。
 *
 * @param triangleIndex 三角形索引。
 * @param vertexOffset 三角形内顶点偏移。
 * @param indexAttribute 源索引属性。
 * @returns 源顶点索引。
 */
function readSourceVertexIndex(
  triangleIndex: number,
  vertexOffset: 0 | 1 | 2,
  indexAttribute: THREE.BufferAttribute | null,
): number {
  return indexAttribute
    ? indexAttribute.getX(triangleIndex * 3 + vertexOffset)
    : triangleIndex * 3 + vertexOffset;
}

/**
 * 根据源位置属性解析源三角形数量。
 *
 * @param positionAttribute 源位置属性。
 * @param indexAttribute 源索引属性。
 * @returns 源三角形数量。
 */
function resolveSourceTriangleCount(
  positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
  indexAttribute: THREE.BufferAttribute | null,
): number {
  return indexAttribute
    ? Math.floor(indexAttribute.count / 3)
    : Math.floor(positionAttribute.count / 3);
}

/**
 * 判断三角形区间是否落在源几何内。
 *
 * @param triangleRange 三角形半开区间。
 * @param sourceTriangleCount 源三角形数量。
 * @returns 区间是否有效。
 */
function isTriangleRangeInsideSource(
  triangleRange: [number, number],
  sourceTriangleCount: number,
): boolean {
  return Number.isInteger(triangleRange[0])
    && Number.isInteger(triangleRange[1])
    && triangleRange[0] >= 0
    && triangleRange[1] > triangleRange[0]
    && triangleRange[1] <= sourceTriangleCount;
}

/**
 * 判断源顶点索引是否可读取。
 *
 * @param vertexIndex 源顶点索引。
 * @param positionAttribute 源位置属性。
 * @returns 是否可读取。
 */
function isValidSourceVertexIndex(
  vertexIndex: number,
  positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
): boolean {
  return Number.isInteger(vertexIndex)
    && vertexIndex >= 0
    && vertexIndex < positionAttribute.count;
}

/**
 * 写入一个新的紧凑顶点。
 *
 * @param input 写入上下文。
 * @returns 紧凑顶点索引。
 */
function writeCompactVertex(input: {
  sourceVertexIndex: number;
  compactVertexIndex: number;
  positionAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute;
  normalAttribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute | null;
  compactPositions: Float32Array;
  compactNormals: Float32Array | null;
}): number {
  const targetOffset = input.compactVertexIndex * 3;
  input.compactPositions[targetOffset] = input.positionAttribute.getX(input.sourceVertexIndex);
  input.compactPositions[targetOffset + 1] = input.positionAttribute.getY(input.sourceVertexIndex);
  input.compactPositions[targetOffset + 2] = input.positionAttribute.getZ(input.sourceVertexIndex);

  if (input.normalAttribute && input.compactNormals) {
    input.compactNormals[targetOffset] = input.normalAttribute.getX(input.sourceVertexIndex);
    input.compactNormals[targetOffset + 1] = input.normalAttribute.getY(input.sourceVertexIndex);
    input.compactNormals[targetOffset + 2] = input.normalAttribute.getZ(input.sourceVertexIndex);
  }

  return input.compactVertexIndex;
}

/**
 * 创建适合顶点数量的索引数组。
 *
 * @param compactIndices 源 Uint32 索引。
 * @param indexCount 实际索引数量。
 * @param compactVertexCount 实际顶点数量。
 * @returns 独立索引数组。
 */
function createCompactIndexArray(
  compactIndices: Uint32Array,
  indexCount: number,
  compactVertexCount: number,
): Uint16Array | Uint32Array {
  const usedIndexView = compactIndices.subarray(0, indexCount);
  return compactVertexCount <= 65535
    ? Uint16Array.from(usedIndexView)
    : Uint32Array.from(usedIndexView);
}

/**
 * 写入紧凑几何包围体。
 *
 * @param geometry 目标几何。
 * @param positions 紧凑位置数组。
 */
function writeCompactGeometryBounds(geometry: THREE.BufferGeometry, positions: Float32Array): void {
  const box = new THREE.Box3();
  const point = new THREE.Vector3();
  for (let cursor = 0; cursor < positions.length; cursor += 3) {
    point.set(positions[cursor] ?? 0, positions[cursor + 1] ?? 0, positions[cursor + 2] ?? 0);
    box.expandByPoint(point);
  }

  const center = new THREE.Vector3();
  box.getCenter(center);

  let radiusSquared = 0;
  for (let cursor = 0; cursor < positions.length; cursor += 3) {
    point.set(positions[cursor] ?? 0, positions[cursor + 1] ?? 0, positions[cursor + 2] ?? 0);
    radiusSquared = Math.max(radiusSquared, center.distanceToSquared(point));
  }

  geometry.boundingBox = box;
  geometry.boundingSphere = new THREE.Sphere(center, Math.sqrt(radiusSquared));
}

/**
 * 从源几何提取指定三角形范围，并生成完全独立的紧凑 BufferGeometry。
 *
 * 设计说明：
 * 1. FeatureFace 代理不能直接共享原始 Mesh 的 BufferAttribute，否则 WebGPU 在不同材质管线之间切换时可能复用错误的顶点缓冲绑定；
 * 2. 这里只复制当前 face 实际引用到的顶点，避免复制整块 GLB primitive，从源头控制 hover/selection 的内存成本；
 * 3. 返回几何由调用方持有并释放，源几何的生命周期不会被代理对象影响。
 *
 * @param input 构建输入。
 * @returns 独立紧凑几何；源数据不合法时返回 `null`。
 */
export function createCompactIndexedFaceGeometry(
  input: CreateCompactIndexedFaceGeometryInput,
): THREE.BufferGeometry | null {
  const positionAttribute = input.sourceGeometry.getAttribute('position');
  if (!isReadableVectorAttribute(positionAttribute)) {
    return null;
  }

  const indexAttribute = input.sourceGeometry.getIndex();
  const sourceTriangleCount = resolveSourceTriangleCount(positionAttribute, indexAttribute);
  if (!isTriangleRangeInsideSource(input.triangleRange, sourceTriangleCount)) {
    return null;
  }

  const triangleCount = input.triangleRange[1] - input.triangleRange[0];
  const maxIndexCount = triangleCount * 3;
  const sourceToCompactIndex = new Map<number, number>();
  const compactIndices = new Uint32Array(maxIndexCount);
  const compactPositions = new Float32Array(maxIndexCount * 3);
  const sourceNormalAttribute = input.includeNormal === false
    ? null
    : input.sourceGeometry.getAttribute('normal');
  const normalAttribute = isReadableVectorAttribute(sourceNormalAttribute) ? sourceNormalAttribute : null;
  const compactNormals = normalAttribute ? new Float32Array(maxIndexCount * 3) : null;

  let indexWriteCursor = 0;
  for (let triangleIndex = input.triangleRange[0]; triangleIndex < input.triangleRange[1]; triangleIndex += 1) {
    for (let vertexOffset = 0; vertexOffset < 3; vertexOffset += 1) {
      const sourceVertexIndex = readSourceVertexIndex(triangleIndex, vertexOffset as 0 | 1 | 2, indexAttribute);
      if (!isValidSourceVertexIndex(sourceVertexIndex, positionAttribute)) {
        return null;
      }

      let compactVertexIndex = sourceToCompactIndex.get(sourceVertexIndex);
      if (typeof compactVertexIndex !== 'number') {
        compactVertexIndex = writeCompactVertex({
          sourceVertexIndex,
          compactVertexIndex: sourceToCompactIndex.size,
          positionAttribute,
          normalAttribute,
          compactPositions,
          compactNormals,
        });
        sourceToCompactIndex.set(sourceVertexIndex, compactVertexIndex);
      }

      compactIndices[indexWriteCursor] = compactVertexIndex;
      indexWriteCursor += 1;
    }
  }

  const compactVertexCount = sourceToCompactIndex.size;
  if (compactVertexCount <= 0 || indexWriteCursor <= 0) {
    return null;
  }

  const usedPositions = compactPositions.slice(0, compactVertexCount * 3);
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(usedPositions, 3));

  if (compactNormals) {
    geometry.setAttribute(
      'normal',
      new THREE.BufferAttribute(compactNormals.slice(0, compactVertexCount * 3), 3),
    );
  }

  geometry.setIndex(new THREE.BufferAttribute(
    createCompactIndexArray(compactIndices, indexWriteCursor, compactVertexCount),
    1,
  ));
  writeCompactGeometryBounds(geometry, usedPositions);
  return geometry;
}
